/* 
 * Copyright (C) 2022 SHC Holdings LLC.  All Rights Reserved. 
 */
import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { Form, Label, FormGroup, Input, FormFeedback, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';
import { Button, isEmpty, MessageBox, PASSWORD_PATTERN, useAPI, useForm, PASSWORD_RULE_MSG, PASSWORD_DESCRIPTION, useAuth } from 'nmqf-common-ui';

const Description = styled.div`
  margin: -0.5rem 0 0.75rem;
  font-size: 80%;
`;

const validationSchema = Yup.lazy((values: any) =>
  Yup.object().shape({
    new_password: Yup.string().matches(PASSWORD_PATTERN, PASSWORD_RULE_MSG).required('Required'),
    passwordConfirmation: Yup.lazy((value) =>
      isEmpty(value) ? Yup.string().required('Required') : Yup.string().oneOf([values.new_password], 'Passwords do not match')
    ),
  })
);

interface ChangePasswordRequiredModalProps {
  isOpen: boolean;
  sessionId?: string;
  username?: string;
  toggle: () => void;
}

export const ChangePasswordRequiredModal: React.FC<ChangePasswordRequiredModalProps> = (props) => {
  const [changePasswordData, changePassword, resetStore] = useAPI('/api/auth/changePassword', undefined, 'POST');
  const auth = useAuth();

  const { setInitialValues, values, handleChange, handleBlur, handleSubmit, isSubmitting, getError } = useForm({ validationSchema });

  useEffect(() => {
    setInitialValues({
      new_password: '',
      passwordConfirmation: '',
      username: props.username,
      session_id: props.sessionId,
    });
    return resetStore;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  useEffect(() => {
    if (changePasswordData.data) {
      props.toggle();
      auth.jwt = changePasswordData.data.JWT;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changePasswordData.data]);

  const onSubmit = handleSubmit(async (payload) => {
    await changePassword(payload);
  });

  const toggle = () => {
    if (!isSubmitting) {
      props.toggle();
    }
  };

  return (
    <Modal isOpen={props.isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Change Password Required</ModalHeader>
      <ModalBody>
        <MessageBox error={changePasswordData.error} />
        <Form onSubmit={onSubmit}>
          <FormGroup>
            <Label>New password</Label>
            <Description>{PASSWORD_DESCRIPTION}</Description>
            <Input
              type="password"
              name="new_password"
              value={values.new_password}
              onChange={handleChange}
              onBlur={handleBlur}
              invalid={!!getError('new_password')}
            />
            <FormFeedback>{getError('new_password')}</FormFeedback>
          </FormGroup>
          <FormGroup>
            <Label>Re-type new password</Label>
            <Input
              type="password"
              name="passwordConfirmation"
              value={values.passwordConfirmation}
              onChange={handleChange}
              onBlur={handleBlur}
              invalid={!!getError('passwordConfirmation')}
            />
            <FormFeedback>{getError('passwordConfirmation')}</FormFeedback>
          </FormGroup>
          <Button color="primary" hidden type="submit" />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" type="button" loading={isSubmitting} onClick={onSubmit}>
          Change password
        </Button>{' '}
        <Button type="button" disabled={isSubmitting} onClick={toggle}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};
